import { useState, useEffect, useRef, useCallback } from 'react';
import { Loader } from '@googlemaps/js-api-loader';

/**
 * Custom hook for Google Places Autocomplete address input
 * Returns autocomplete suggestions and handles place selection
 */
export const useAddressAutocomplete = () => {
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isApiLoaded, setIsApiLoaded] = useState(false);
  const autocompleteService = useRef(null);
  const placesService = useRef(null);
  const debounceTimer = useRef(null);

  // Initialize Google Maps API
  useEffect(() => {
    const loadGoogleMapsAPI = async () => {
      try {
        // Check if API is already loaded
        if (window.google && window.google.maps && window.google.maps.places) {
          autocompleteService.current = new window.google.maps.places.AutocompleteService();
          placesService.current = new window.google.maps.places.PlacesService(
            document.createElement('div')
          );
          setIsApiLoaded(true);
          return;
        }

        const loader = new Loader({
          apiKey: process.env.REACT_APP_GOOGLE_API_KEY,
          version: 'weekly',
          libraries: ['places']
        });

        await loader.load();
        
        if (window.google && window.google.maps && window.google.maps.places) {
          autocompleteService.current = new window.google.maps.places.AutocompleteService();
          placesService.current = new window.google.maps.places.PlacesService(
            document.createElement('div')
          );
          setIsApiLoaded(true);
        }
      } catch (error) {
        console.error('Error loading Google Maps API:', error);
      }
    };

    loadGoogleMapsAPI();
  }, []);

  // Search for address suggestions
  const searchAddress = useCallback((input) => {
    // Clear previous debounce timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Clear suggestions if input is too short
    if (!input || input.trim().length < 3) {
      setSuggestions([]);
      setIsLoading(false);
      return;
    }

    // Debounce the API call
    debounceTimer.current = setTimeout(() => {
      if (!isApiLoaded || !autocompleteService.current) {
        return;
      }

      setIsLoading(true);
      autocompleteService.current.getPlacePredictions(
        {
          input: input.trim(),
          componentRestrictions: { country: 'de' },
          types: ['address']
        },
        (predictions, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
            setSuggestions(predictions);
          } else {
            setSuggestions([]);
          }
          setIsLoading(false);
        }
      );
    }, 300);
  }, [isApiLoaded]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  // Parse Google address components into structured data
  const parseAddressComponents = (components, placeName = '', formattedAddress = '') => {
    const addressData = {
      street: '',
      postalCode: '',
      city: '',
      country: 'Deutschland',
      additionalAddress: ''
    };

    if (!components) return addressData;

    let routeName = '';
    let streetNumber = '';
    let premise = ''; // For building names like "Sport- und Freizeitzentrum"

    components.forEach((component) => {
      const types = component.types;

      if (types.includes('route')) {
        routeName = component.long_name;
      } else if (types.includes('street_number')) {
        streetNumber = component.long_name;
      } else if (types.includes('premise') || types.includes('establishment')) {
        premise = component.long_name;
      } else if (types.includes('postal_code')) {
        addressData.postalCode = component.long_name;
      } else if (types.includes('locality') || types.includes('postal_town')) {
        addressData.city = component.long_name;
      } else if (types.includes('subpremise')) {
        addressData.additionalAddress = component.long_name;
      } else if (types.includes('country')) {
        if (component.short_name === 'DE') {
          addressData.country = 'Deutschland';
        }
      }
    });

    // Combine route and street number in correct order (route + number)
    if (routeName && streetNumber) {
      addressData.street = `${routeName} ${streetNumber}`;
    } else if (routeName) {
      addressData.street = routeName;
      // If we have a premise but no street number, try to extract from formatted address
      if (premise && formattedAddress) {
        // Try to find a number in the formatted address after the premise name
        const premiseIndex = formattedAddress.indexOf(premise);
        if (premiseIndex !== -1) {
          const afterPremise = formattedAddress.substring(premiseIndex + premise.length);
          const numberMatch = afterPremise.match(/\s+(\d+[a-z]?)/i);
          if (numberMatch) {
            addressData.street = `${routeName} ${numberMatch[1]}`;
          } else {
            // If no number found, use premise + route as fallback
            addressData.street = premise;
          }
        }
      }
    } else if (streetNumber) {
      addressData.street = streetNumber;
    } else if (premise) {      
      // Try to extract street with number from formatted address
      // Format is usually: "Premise Name, Street Number, Postal Code City"
      if (formattedAddress) {
        // Remove premise name and try to find street pattern
        const withoutPremise = formattedAddress.replace(new RegExp(premise, 'i'), '').trim();
        // Look for pattern like "Street Name Number" or "Street Name, Number"
        const streetMatch = withoutPremise.match(/^([^,]+?)\s*,\s*(\d+[a-z]?)/i) || 
                          withoutPremise.match(/^([^,]+?)\s+(\d+[a-z]?)/i);
        
        if (streetMatch) {
          addressData.street = `${streetMatch[1].trim()} ${streetMatch[2]}`;
        } else {
          // Last resort: use premise name (but this will fail validation)
          addressData.street = premise;
        }
      } else {
        addressData.street = premise;
      }
    }

    return addressData;
  };

  // Helper function to get address from coordinates using Geocoding API
  const getAddressFromCoordinates = async (coordinates) => {
    const apiKey = process.env.REACT_APP_GOOGLE_API_KEY;
    if (!apiKey) {
      throw new Error('Google API key not found');
    }

    const response = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${coordinates.lat},${coordinates.lng}&key=${apiKey}&language=de`
    );

    if (!response.ok) {
      throw new Error('Geocoding API request failed');
    }

    const data = await response.json();
    
    if (data.status === 'OK' && data.results && data.results.length > 0) {
      const result = data.results[0];
      return parseAddressComponents(
        result.address_components, 
        result.formatted_address, 
        result.formatted_address
      );
    }

    throw new Error('No results from geocoding API');
  };

  // Get place details and parse address components
  const selectPlace = useCallback((placeId) => {
    return new Promise((resolve, reject) => {
      if (!isApiLoaded || !placesService.current) {
        reject(new Error('Google Places API not loaded yet'));
        return;
      }

      placesService.current.getDetails(
        {
          placeId,
          fields: [
            'address_components',
            'formatted_address',
            'geometry',
            'name'
          ]
        },
        async (place, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
            
            let addressData = parseAddressComponents(
              place.address_components, 
              place.name, 
              place.formatted_address
            );
            
            
            const coordinates = {
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng()
            };
            
            // If street doesn't have a number, try reverse geocoding to get the actual street address
            if (addressData.street && !/\d/.test(addressData.street)) {
              try {
                const geocodeData = await getAddressFromCoordinates(coordinates);
                if (geocodeData.street && /\d/.test(geocodeData.street)) {
                  addressData.street = geocodeData.street;
                }
              } catch (error) {
                console.warn('[useAddressAutocomplete] Could not get street from geocoding:', error);
              }
            }
            
            // If postal code is missing, try to extract it from formatted_address
            if (!addressData.postalCode && place.formatted_address) {
              const postalCodeMatch = place.formatted_address.match(/\b\d{5}\b/);
              if (postalCodeMatch) {
                addressData.postalCode = postalCodeMatch[0];
              } else {
                // If still not found, try reverse geocoding to get more complete address info
                try {
                  const geocodeData = await getAddressFromCoordinates(coordinates);
                  if (geocodeData.postalCode) {
                    addressData.postalCode = geocodeData.postalCode;
                  }
                  // Also update city if it was missing
                  if (!addressData.city && geocodeData.city) {
                    addressData.city = geocodeData.city;
                  }
                } catch (error) {
                  console.warn('Could not get postal code from geocoding:', error);
                }
              }
            }
            
            resolve({
              ...addressData,
              coordinates,
              formattedAddress: place.formatted_address
            });
          } else {
            reject(new Error('Failed to get place details'));
          }
        }
      );
    });
  }, [isApiLoaded]);

  const clearSuggestions = useCallback(() => {
    setSuggestions([]);
  }, []);

  return {
    suggestions,
    isLoading,
    isApiLoaded,
    searchAddress,
    selectPlace,
    clearSuggestions
  };
};

